import catalog from "../data/catalog.json";
import content from "../data/content.json";

const { categories: CATEGORY_DATA, products: PRODUCTS } = catalog;
const { blogPosts: BLOG_POSTS, vehicles: VEHICLES } = content;

const VAT_RATE = 0.21;
const FACET_SPEC_KEYS = [
  "Viskozitāte",
  "Viskozitātes klase",
  "Tips",
  "Temperatūra",
  "Krāsa",
  "Spriegums",
  "Jauda",
  "Cokols",
  "Garums",
  "Kapacitāte",
  "Aromāts",
  "Standarts",
];

const STORAGE_PREFIX = "dansg_demo_";
const STORAGE = {
  session: `${STORAGE_PREFIX}session`,
  users: `${STORAGE_PREFIX}users`,
  orders: `${STORAGE_PREFIX}orders`,
  quotes: `${STORAGE_PREFIX}quotes`,
  newsletter: `${STORAGE_PREFIX}newsletter`,
};

export const DEMO_ACCOUNT = {
  email: "b2b@test.lv",
  password: "Parole123!",
};

const DEMO_B2B_USER = {
  user_id: "user_demo_b2b",
  email: DEMO_ACCOUNT.email,
  name: "Jānis Bērziņš",
  phone: "+371 20000000",
  auth_provider: "demo",
  role: "customer",
  is_b2b: true,
  company_name: "SIA AgroTests",
  reg_number: "40001234567",
  vat_number: "LV40001234567",
  b2b_status: "approved",
  discount_pct: 5,
  created_at: "2026-01-01T09:00:00.000Z",
};

function storageAvailable() {
  return typeof window !== "undefined" && window.localStorage;
}

function readStorage(key, fallback) {
  try {
    if (!storageAvailable()) return fallback;
    const value = window.localStorage.getItem(key);
    if (value == null) return fallback;
    const parsed = JSON.parse(value);
    if (Array.isArray(fallback) && !Array.isArray(parsed)) return fallback;
    return parsed;
  } catch {
    return fallback;
  }
}

function writeStorage(key, value) {
  try {
    if (storageAvailable()) window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // The demo remains usable when storage is blocked or full.
  }
}

function removeStorage(key) {
  try {
    if (storageAvailable()) window.localStorage.removeItem(key);
  } catch {
    // Ignore unavailable browser storage.
  }
}

function apiError(status, detail) {
  const error = new Error(typeof detail === "string" ? detail : "Demo API error");
  error.response = { status, data: { detail } };
  return error;
}

function makeId(prefix) {
  const random = globalThis.crypto?.randomUUID?.() || `${Date.now()}_${Math.random().toString(16).slice(2)}`;
  return `${prefix}_${random}`;
}

function currentUser(required = false) {
  const stored = readStorage(STORAGE.session, null);
  const user = stored && typeof stored === "object" && !Array.isArray(stored) && stored.user_id ? stored : null;
  if (!user && required) throw apiError(401, "Nav autorizēts");
  return user;
}

function normalizeParams(params = {}) {
  return Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ""));
}

function contains(value, query) {
  return String(value || "").toLocaleLowerCase("lv").includes(String(query).toLocaleLowerCase("lv"));
}

function parseAttrs(value = "") {
  const grouped = {};
  String(value).split("||").forEach((token) => {
    const separator = token.indexOf(":");
    if (separator === -1) return;
    const key = token.slice(0, separator);
    const itemValue = token.slice(separator + 1);
    if (key && itemValue) (grouped[key] ||= []).push(itemValue);
  });
  return grouped;
}

function filterProducts(params, { includeFacets = true } = {}) {
  const brands = params.brand ? String(params.brand).split(",") : [];
  const skus = params.skus ? String(params.skus).split(",").map((value) => value.trim()).filter(Boolean) : [];
  const volumes = params.volume ? String(params.volume).split(",") : [];
  const attrs = parseAttrs(params.attrs);

  return PRODUCTS.filter((product) => {
    if (params.category && product.category !== params.category) return false;
    if (params.featured !== undefined && product.featured !== (params.featured === true || params.featured === "true")) return false;
    if (params.q && ![product.name, product.sku, product.brand].some((value) => contains(value, params.q))) return false;
    if (skus.length && !skus.includes(product.sku)) return false;
    if (includeFacets && brands.length && !brands.includes(product.brand)) return false;
    if (includeFacets && volumes.length && !volumes.includes(product.volume)) return false;
    if (includeFacets && Object.entries(attrs).some(([key, values]) => !values.includes(product.specs?.[key]))) return false;
    return true;
  });
}

function facetValues(products, getter, limit) {
  const counts = new Map();
  products.forEach((product) => {
    const value = getter(product);
    if (value) counts.set(value, (counts.get(value) || 0) + 1);
  });
  return [...counts.entries()]
    .sort(([aValue, aCount], [bValue, bCount]) => bCount - aCount || String(aValue).localeCompare(String(bValue), "lv"))
    .slice(0, limit)
    .map(([value, count]) => ({ value, count }));
}

function getProductList(rawParams = {}) {
  const params = normalizeParams(rawParams);
  const page = Math.max(1, Number.parseInt(params.page || "1", 10) || 1);
  const limit = Math.min(100, Math.max(1, Number.parseInt(params.limit || "24", 10) || 24));
  const filtered = filterProducts(params);
  const facetBase = filterProducts(params, { includeFacets: false });
  const sorted = [...filtered].sort((a, b) => {
    if (params.sort === "price_asc") return a.price_tiers[0].price - b.price_tiers[0].price;
    if (params.sort === "price_desc") return b.price_tiers[0].price - a.price_tiers[0].price;
    return a.name.localeCompare(b.name, "lv");
  });

  const brands = [...new Set(facetBase.map((product) => product.brand))].sort((a, b) => a.localeCompare(b, "lv"));
  const facets = [];
  const volumes = facetValues(facetBase, (product) => product.volume, 12);
  if (volumes.length > 1) facets.push({ key: "Tilpums / Fasējums", param: "volume", values: volumes });

  FACET_SPEC_KEYS.forEach((key) => {
    const values = facetValues(facetBase, (product) => product.specs?.[key], 10);
    const representedProducts = values.reduce((sum, item) => sum + item.count, 0);
    if (values.length > 1 && representedProducts >= 3) facets.push({ key, param: "attrs", values });
  });

  const start = (page - 1) * limit;
  return {
    items: sorted.slice(start, start + limit),
    total: sorted.length,
    brands,
    facets,
    page,
    limit,
  };
}

function getCategories() {
  const counts = PRODUCTS.reduce((result, product) => {
    result[product.category] = (result[product.category] || 0) + 1;
    return result;
  }, {});
  return CATEGORY_DATA.map((category) => ({ ...category, count: counts[category.id] || 0 }));
}

function getProductDetail(slug) {
  const product = PRODUCTS.find((item) => item.slug === slug);
  if (!product) throw apiError(404, "Produkts nav atrasts");
  const related = PRODUCTS.filter((item) => item.category === product.category && item.slug !== slug).slice(0, 4);
  return { ...product, related };
}

function suggestProducts(query) {
  if (String(query || "").length < 2) return [];
  return PRODUCTS.filter((product) => [product.name, product.sku, product.brand].some((value) => contains(value, query)))
    .slice(0, 8)
    .map(({ sku, slug, name, brand, price_tiers, image, stock }) => ({ sku, slug, name, brand, price_tiers, image, stock }));
}

function lookupProducts(items = []) {
  return items.map((item) => {
    const product = PRODUCTS.find((candidate) => candidate.sku.toLocaleLowerCase("lv") === String(item.sku || "").trim().toLocaleLowerCase("lv"));
    return { sku: item.sku, qty: item.qty, found: Boolean(product), product: product || null };
  });
}

function login(payload) {
  const email = String(payload.email || "").trim().toLowerCase();
  const password = String(payload.password || "");
  let user = null;

  if (email === DEMO_ACCOUNT.email && password === DEMO_ACCOUNT.password) {
    user = DEMO_B2B_USER;
  } else {
    const registered = readStorage(STORAGE.users, []);
    user = registered.find((candidate) => candidate.email === email) || null;
    if (user && password.length < 6) user = null;
  }

  if (!user) throw apiError(401, "Nepareizs e-pasts vai parole");
  writeStorage(STORAGE.session, user);
  return user;
}

function register(payload) {
  const email = String(payload.email || "").trim().toLowerCase();
  if (!email.includes("@")) throw apiError(400, "Nederīga e-pasta adrese");
  if (String(payload.password || "").length < 6) throw apiError(422, "Parolei jābūt vismaz 6 simbolus garai");

  const users = readStorage(STORAGE.users, []);
  if (email === DEMO_ACCOUNT.email || users.some((user) => user.email === email)) {
    throw apiError(400, "Lietotājs ar šādu e-pastu jau eksistē");
  }

  const user = {
    user_id: makeId("user"),
    email,
    name: payload.name,
    phone: payload.phone || null,
    auth_provider: "demo",
    role: "customer",
    is_b2b: Boolean(payload.is_b2b),
    company_name: payload.is_b2b ? payload.company_name || null : null,
    reg_number: payload.is_b2b ? payload.reg_number || null : null,
    vat_number: payload.is_b2b ? payload.vat_number || null : null,
    b2b_status: payload.is_b2b ? "pending" : null,
    discount_pct: 0,
    created_at: new Date().toISOString(),
  };
  writeStorage(STORAGE.users, [...users, user]);
  writeStorage(STORAGE.session, user);
  return user;
}

function createOrder(payload) {
  if (!payload.items?.length) throw apiError(400, "Grozs ir tukšs");
  const user = currentUser(false);
  const discountPct = user?.discount_pct || 0;
  let subtotal = 0;
  const items = payload.items.map((item) => {
    const product = PRODUCTS.find((candidate) => candidate.sku === item.sku);
    if (!product) throw apiError(400, `Produkts ${item.sku} nav atrasts`);
    let unitPrice = tierPrice(product.price_tiers, item.qty);
    if (discountPct) unitPrice = Number((unitPrice * (1 - discountPct / 100)).toFixed(2));
    const total = Number((unitPrice * item.qty).toFixed(2));
    subtotal += total;
    return { sku: product.sku, name: product.name, brand: product.brand, qty: item.qty, unit_price: unitPrice, total };
  });

  const orders = readStorage(STORAGE.orders, []);
  subtotal = Number(subtotal.toFixed(2));
  const vat = Number((subtotal * VAT_RATE).toFixed(2));
  const order = {
    order_id: makeId("order"),
    number: `DG-${1001 + orders.length}`,
    user_id: user?.user_id || null,
    customer: payload.customer,
    delivery: payload.delivery,
    items,
    subtotal,
    vat,
    total: Number((subtotal + vat).toFixed(2)),
    discount_pct: discountPct,
    payment_method: "invoice",
    status: "new",
    created_at: new Date().toISOString(),
  };
  writeStorage(STORAGE.orders, [...orders, order]);
  return order;
}

function trackOrder(number) {
  const normalized = String(number || "").trim().toUpperCase();
  const order = readStorage(STORAGE.orders, []).find((candidate) => candidate.number === normalized);
  if (!order) throw apiError(404, "Pasūtījums ar šādu kodu nav atrasts");
  return {
    number: order.number,
    status: order.status,
    created_at: order.created_at,
    total: order.total,
    items_count: order.items.reduce((sum, item) => sum + item.qty, 0),
    delivery_method: order.delivery.method,
    payment_method: order.payment_method,
  };
}

function createQuote(payload) {
  const user = currentUser(false);
  const quotes = readStorage(STORAGE.quotes, []);
  const quote = {
    quote_id: makeId("quote"),
    number: `RFQ-${1001 + quotes.length}`,
    user_id: user?.user_id || null,
    ...payload,
    status: "new",
    created_at: new Date().toISOString(),
  };
  writeStorage(STORAGE.quotes, [...quotes, quote]);
  return quote;
}

function subscribeNewsletter(payload) {
  const email = String(payload.email || "").trim().toLowerCase();
  if (!email.includes("@") || !email.includes(".")) throw apiError(400, "Nederīga e-pasta adrese");
  const subscriptions = readStorage(STORAGE.newsletter, []);
  if (!subscriptions.includes(email)) writeStorage(STORAGE.newsletter, [...subscriptions, email]);
  return { ok: true };
}

function decodeTail(path, prefix) {
  return decodeURIComponent(path.slice(prefix.length));
}

async function get(path, options = {}) {
  const params = normalizeParams(options.params);
  let data;

  if (path === "/categories") data = getCategories();
  else if (path === "/products") data = getProductList(params);
  else if (path === "/products/suggest") data = suggestProducts(params.q);
  else if (path.startsWith("/products/")) data = getProductDetail(decodeTail(path, "/products/"));
  else if (path === "/auth/me") data = currentUser(true);
  else if (path.startsWith("/orders/track/")) data = trackOrder(decodeTail(path, "/orders/track/"));
  else if (path === "/orders") {
    const user = currentUser(true);
    data = readStorage(STORAGE.orders, []).filter((order) => order.user_id === user.user_id).sort((a, b) => b.created_at.localeCompare(a.created_at));
  } else if (path === "/quotes") {
    const user = currentUser(true);
    data = readStorage(STORAGE.quotes, []).filter((quote) => quote.user_id === user.user_id).sort((a, b) => b.created_at.localeCompare(a.created_at));
  } else if (path === "/blog") data = BLOG_POSTS.map((post) => {
    const summary = { ...post };
    delete summary.body;
    return summary;
  });
  else if (path.startsWith("/blog/")) {
    data = BLOG_POSTS.find((post) => post.slug === decodeTail(path, "/blog/"));
    if (!data) throw apiError(404, "Raksts nav atrasts");
  } else if (path === "/vehicles") data = VEHICLES;
  else if (path === "/") data = { message: "DANS G demo API" };
  else throw apiError(404, "Demo resurss nav atrasts");

  return { data };
}

async function post(path, payload = {}) {
  let data;
  if (path === "/auth/login") data = login(payload);
  else if (path === "/auth/register") data = register(payload);
  else if (path === "/auth/demo" || path === "/auth/session") {
    data = DEMO_B2B_USER;
    writeStorage(STORAGE.session, data);
  } else if (path === "/auth/logout") {
    removeStorage(STORAGE.session);
    data = { ok: true };
  } else if (path === "/auth/refresh") data = { ok: Boolean(currentUser(true)) };
  else if (path === "/products/lookup") data = lookupProducts(payload.items);
  else if (path === "/orders") data = createOrder(payload);
  else if (path === "/quotes") data = createQuote(payload);
  else if (path === "/newsletter") data = subscribeNewsletter(payload);
  else throw apiError(404, "Demo resurss nav atrasts");
  return { data };
}

export const api = { get, post };

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Radās kļūda. Lūdzu, mēģiniet vēlreiz.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function formatPrice(v) {
  return `${Number(v).toFixed(2).replace(".", ",")} €`;
}

export function tierPrice(tiers, qty) {
  let price = tiers[0].price;
  [...tiers].sort((a, b) => a.min_qty - b.min_qty).forEach((tier) => {
    if (qty >= tier.min_qty) price = tier.price;
  });
  return price;
}

export const __demoStorageKeys = STORAGE;
